import { useMediaQuery } from '@mui/material';

/**
 * Responsive font size utility for mobile screens
 * Provides smaller, proportional font sizes based on screen size
 */
export const useResponsiveFonts = () => {
  const isSmallMobile = useMediaQuery('(max-width: 360px)');
  const isMediumMobile = useMediaQuery('(max-width: 480px)');
  const isLargeMobile = useMediaQuery('(max-width: 768px)');

  // Font size scale factors - Much more aggressive scaling for mobile
  const getFontScale = () => {
    if (isSmallMobile) return 0.25; // Very small screens - 75% reduction
    if (isMediumMobile) return 0.3; // Small screens - 70% reduction
    if (isLargeMobile) return 0.4; // Medium screens - 60% reduction
    return 1; // Default
  };

  const scale = getFontScale();

  return {
    // Header fonts
    header: {
      xs: `${Math.round(12 * scale)}px`,
      sm: `${Math.round(14 * scale)}px`,
      md: `${Math.round(16 * scale)}px`
    },
    
    // Title fonts
    title: {
      xs: `${Math.round(10 * scale)}px`,
      sm: `${Math.round(12 * scale)}px`,
      md: `${Math.round(14 * scale)}px`
    },
    
    // Body fonts
    body: {
      xs: `${Math.round(8 * scale)}px`,
      sm: `${Math.round(9 * scale)}px`,
      md: `${Math.round(10 * scale)}px`
    },
    
    // Small fonts
    small: {
      xs: `${Math.round(6 * scale)}px`,
      sm: `${Math.round(7 * scale)}px`,
      md: `${Math.round(8 * scale)}px`
    },
    
    // Extra small fonts
    extraSmall: {
      xs: `${Math.round(5 * scale)}px`,
      sm: `${Math.round(6 * scale)}px`,
      md: `${Math.round(7 * scale)}px`
    },
    
    // Caption fonts
    caption: {
      xs: `${Math.round(4 * scale)}px`,
      sm: `${Math.round(5 * scale)}px`,
      md: `${Math.round(6 * scale)}px`
    },
    
    // Icon sizes
    icon: {
      xs: Math.round(14 * scale),
      sm: Math.round(16 * scale),
      md: Math.round(18 * scale)
    },
    
    // Small icon sizes
    iconSmall: {
      xs: Math.round(10 * scale),
      sm: Math.round(12 * scale),
      md: Math.round(14 * scale)
    },
    
    // Avatar sizes
    avatar: {
      xs: Math.round(28 * scale),
      sm: Math.round(32 * scale),
      md: Math.round(36 * scale)
    },
    
    // Button sizes
    button: {
      xs: `${Math.round(6 * scale)}px`,
      sm: `${Math.round(7 * scale)}px`,
      md: `${Math.round(8 * scale)}px`
    }
  };
};

/**
 * Get responsive truncation length based on screen size
 */
const getResponsiveTruncationLength = (baseLength) => {
  const width = window.innerWidth;
  if (width <= 360) return Math.floor(baseLength * 0.6); // Very small screens
  if (width <= 480) return Math.floor(baseLength * 0.7); // Small screens
  if (width <= 768) return Math.floor(baseLength * 0.8); // Medium screens
  if (width <= 1024) return Math.floor(baseLength * 0.9); // Tablets
  return baseLength; // Desktop
};

/**
 * Text truncation utility for mobile components - responsive to screen size
 */
export const truncateText = (text, baseMaxLength = 15) => {
  if (!text) return 'N/A';
  const maxLength = getResponsiveTruncationLength(baseMaxLength);
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

/**
 * Truncate name with smart word boundary - responsive to screen size
 * Never truncate if it's a short name (less than 3 words)
 */
export const truncateName = (name, baseMaxLength = 15) => {
  if (!name) return 'N/A';
  
  // Don't truncate short names
  if (name.length <= 8) return name;
  
  const maxLength = getResponsiveTruncationLength(baseMaxLength);
  if (name.length <= maxLength) return name;
  
  const words = name.split(' ');
  
  // If it's a very short name (1-2 words), don't truncate
  if (words.length <= 2 && name.length <= 15) return name;
  
  let result = '';
  
  for (const word of words) {
    if ((result + ' ' + word).trim().length <= maxLength) {
      result += (result ? ' ' : '') + word;
    } else {
      break;
    }
  }
  
  return result.length > 0 ? result + '...' : name.substring(0, maxLength) + '...';
};

/**
 * Truncate vehicle model with smart word boundary - responsive to screen size
 */
export const truncateVehicleModel = (model, baseMaxLength = 18) => {
  if (!model) return 'N/A';
  
  // Don't truncate short vehicle models
  if (model.length <= 12) return model;
  
  const maxLength = getResponsiveTruncationLength(baseMaxLength);
  if (model.length <= maxLength) return model;
  
  const words = model.split(' ');
  let result = '';
  
  for (const word of words) {
    if ((result + ' ' + word).trim().length <= maxLength) {
      result += (result ? ' ' : '') + word;
    } else {
      break;
    }
  }
  
  return result.length > 0 ? result + '...' : model.substring(0, maxLength) + '...';
};

/**
 * Never truncate vehicle numbers - they should always be visible
 */
export const truncateVehicleNumber = (number) => {
  if (!number) return 'N/A';
  return number; // Never truncate vehicle numbers
};

/**
 * Never truncate phone numbers - they should always be visible
 */
export const truncatePhoneNumber = (phone) => {
  if (!phone) return 'N/A';
  return phone; // Never truncate phone numbers
};

/**
 * Get responsive font size for a specific breakpoint
 */
export const getResponsiveFontSize = (baseSize, breakpoint = 'xs') => {
  const isSmallMobile = window.innerWidth <= 360;
  const isMediumMobile = window.innerWidth <= 480;
  const isLargeMobile = window.innerWidth <= 768;

  let scale = 1;
  if (isSmallMobile) scale = 0.75;
  else if (isMediumMobile) scale = 0.85;
  else if (isLargeMobile) scale = 0.9;

  return `${Math.round(baseSize * scale)}px`;
};
